/**
 * Reads src/design/tokens.css as data: used by the contrast and flow tests and by the
 * /design-system page (which loads the file with `?raw`). Only understands what tokens.css uses: `--name: value;` and `var(--x)`.
 */
export type TokenMap = Record<string, string>;

export function parseTokens(css: string): TokenMap {
  const out: TokenMap = {};
  const body = css.replace(/\/\*[\s\S]*?\*\//g, "");
  for (const m of body.matchAll(/(--[\w-]+)\s*:\s*([^;]+);/g)) {
    out[m[1]!] = m[2]!.trim();
  }
  return out;
}

/** Follow `var(--x)` references until a raw value. Throws on unknown names or cycles. */
export function resolveToken(name: string, tokens: TokenMap, seen: string[] = []): string {
  if (seen.includes(name)) throw new Error(`token cycle: ${[...seen, name].join(" -> ")}`);
  const value = tokens[name];
  if (value === undefined) throw new Error(`unknown token ${name}`);
  return value.replace(/var\((--[\w-]+)\)/g, (_m, ref: string) => resolveToken(ref, tokens, [...seen, name]));
}

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.trim().replace("#", "");
  const full = h.length === 3 ? [...h].map((c) => c + c).join("") : h;
  if (!/^[0-9a-f]{6}$/i.test(full)) throw new Error(`not a hex colour: ${hex}`);
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as [number, number, number];
}

export function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG 2.x contrast ratio between two hex colours. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Text/background pairs the UI uses. `min` is 4.5 for text, 3 for icons and UI parts
 * (WCAG 1.4.11). Add a pair here whenever a component puts one colour on another.
 */
export const CONTRAST_PAIRS: { fg: string; bg: string; min: number; use: string }[] = [
  { fg: "--color-fg", bg: "--color-surface", min: 4.5, use: "body text on cards" },
  { fg: "--color-fg", bg: "--color-canvas", min: 4.5, use: "body text on the page" },
  { fg: "--color-fg-muted", bg: "--color-surface", min: 4.5, use: "hints on cards" },
  { fg: "--color-fg-muted", bg: "--color-canvas", min: 4.5, use: "hints on the page" },
  { fg: "--color-fg-muted", bg: "--color-surface-muted", min: 4.5, use: "table head text" },
  { fg: "--color-fg-subtle", bg: "--color-surface", min: 3, use: "icons" },
  { fg: "--color-heading", bg: "--color-surface", min: 4.5, use: "headings" },
  { fg: "--color-heading", bg: "--color-canvas", min: 4.5, use: "headings on the page" },
  { fg: "--color-heading", bg: "--color-secondary-subtle", min: 4.5, use: "active nav item" },
  { fg: "--color-link", bg: "--color-surface", min: 4.5, use: "links" },
  { fg: "--color-link-hover", bg: "--color-surface", min: 4.5, use: "hovered links" },
  { fg: "--color-primary-fg", bg: "--color-primary", min: 4.5, use: "primary button" },
  { fg: "--color-primary-fg", bg: "--color-primary-hover", min: 4.5, use: "primary button hover" },
  { fg: "--color-secondary-fg", bg: "--color-secondary", min: 4.5, use: "secondary filled" },
  { fg: "--color-secondary-fg", bg: "--color-secondary-hover", min: 4.5, use: "secondary hover" },
  { fg: "--color-secondary-subtle-fg", bg: "--color-secondary-subtle", min: 4.5, use: "badges" },
  { fg: "--color-secondary-subtle-fg", bg: "--color-secondary-muted", min: 4.5, use: "pressed outline button" },
  { fg: "--color-fg-inverse", bg: "--color-surface-inverse", min: 4.5, use: "sidebar text" },
  { fg: "--color-fg-inverse-muted", bg: "--color-surface-inverse", min: 4.5, use: "sidebar secondary text" },
  { fg: "--color-accent", bg: "--color-surface", min: 3, use: "accent icons, progress" },
  { fg: "--color-brand", bg: "--color-surface", min: 3, use: "current-step marks (non-text)" },
  { fg: "--color-focus-ring", bg: "--color-surface", min: 3, use: "focus ring" },
  { fg: "--color-focus-ring", bg: "--color-canvas", min: 3, use: "focus ring on page" },
  { fg: "--color-border-input", bg: "--color-surface", min: 3, use: "input, checkbox and radio borders" },
  { fg: "--color-danger", bg: "--color-surface", min: 4.5, use: "error text" },
  { fg: "--color-fg-inverse", bg: "--color-danger", min: 4.5, use: "danger button" },
  { fg: "--color-fg-inverse", bg: "--color-danger-fg", min: 4.5, use: "danger button hover" },
  { fg: "--color-primary-fg", bg: "--color-secondary-hover", min: 4.5, use: "avatar on sidebar" },
  { fg: "--color-danger-fg", bg: "--color-danger-subtle", min: 4.5, use: "error banner" },
  { fg: "--color-success-fg", bg: "--color-success-subtle", min: 4.5, use: "success banner" },
  { fg: "--color-success", bg: "--color-surface", min: 4.5, use: "success text" },
  { fg: "--color-warning-fg", bg: "--color-warning-subtle", min: 4.5, use: "warning banner" },
  { fg: "--color-info-fg", bg: "--color-info-subtle", min: 4.5, use: "info banner" },
  ...["requested", "discovery", "design", "build", "live", "paused", "closed"].map((s) => ({
    fg: `--color-status-${s}-fg`,
    bg: `--color-status-${s}-bg`,
    min: 4.5,
    use: `status pill: ${s}`,
  })),
];
